import React, { useState, useEffect } from 'react';
import { Smartphone, Send, RotateCcw, Play, Terminal } from 'lucide-react';
import { sendLiveUssdRequest, fetchMnoConfigs } from '../services/api';
import { useTelemetry } from '../context/TelemetryContext';

export default function SimulatorView(props) {
  const telemetry = useTelemetry();
  const onSessionCompleted = props.onSessionCompleted || (() => {
    setTimeout(telemetry.loadDashboardData, 800);
  });
  const [mno, setMno] = useState('airtel');
  const [msisdn, setMsisdn] = useState('256751988242');
  const [sessionId, setSessionId] = useState(`SIM_${Date.now()}`);
  const [userInput, setUserInput] = useState('*185#');
  const [hopCount, setHopCount] = useState(0);
  const [mnoOptions, setMnoOptions] = useState([]);
  const [dialogueHistory, setDialogueHistory] = useState([
    { role: 'system', text: 'USSD Dialogue Ready. Dial *185# to initiate session.' }
  ]);
  const [payloadLogs, setPayloadLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchMnoConfigs().then(cfgs => {
      if (cfgs && cfgs.length > 0) {
        setMnoOptions(cfgs);
      }
    });
  }, []);

  const handleSend = async (customInput = null) => {
    const inputToSend = customInput !== null ? customInput : userInput;
    if (!inputToSend.trim()) return;

    setLoading(true);
    const isNew = hopCount === 0;

    setDialogueHistory(prev => [...prev, { role: 'user', text: inputToSend }]);
    setUserInput('');

    try {
      const startTime = Date.now();
      const res = await sendLiveUssdRequest({
        mno,
        sessionId,
        msisdn,
        newRequest: isNew ? '1' : '0',
        input: inputToSend,
      });
      const latency = Date.now() - startTime;

      let displayText = 'No response text received.';
      const match = res.rawXml?.match(/<applicationResponse>([\s\S]*?)<\/applicationResponse>/);
      if (match && match[1]) displayText = match[1];

      setDialogueHistory(prev => [...prev, { role: 'ussd', text: displayText }]);
      setHopCount(prev => prev + 1);

      // Append to protocol logs
      setPayloadLogs(prev => [
        {
          timestamp: new Date().toLocaleTimeString(),
          hop: hopCount + 1,
          input: inputToSend,
          latency: `${latency}ms`,
          status: res.status,
          rawXml: res.rawXml,
        },
        ...prev
      ]);

      if (res.rawXml?.includes('<freeFlowState>FB</freeFlowState>') || res.rawXml?.includes('<freeflowState>FB</freeflowState>')) {
        if (onSessionCompleted) onSessionCompleted();
      }
    } catch (e) {
      setDialogueHistory(prev => [...prev, { role: 'error', text: `Connection error: ${e.message}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const newId = `SIM_${Date.now()}`;
    setSessionId(newId);
    setHopCount(0);
    setUserInput('*185#');
    setDialogueHistory([
      { role: 'system', text: 'Session reset. Dial *185# to start a new session.' }
    ]);
  };

  const handleQuickFlow = async (type) => {
    handleReset();
    const currentSession = `SIM_${Date.now()}`;
    setSessionId(currentSession);
    setLoading(true);

    if (type === 'BALANCE') {
      // Step 1: *185# (Dial shortcode -> PIN prompt)
      setDialogueHistory([{ role: 'user', text: '*185#' }]);
      const r1 = await sendLiveUssdRequest({ mno, sessionId: currentSession, msisdn, newRequest: '1', input: '*185#' });
      let text1 = 'Welcome to Absa Bank\nPlease enter your 4-digit PIN:';
      const m1 = r1.rawXml?.match(/<applicationResponse>([\s\S]*?)<\/applicationResponse>/);
      if (m1 && m1[1]) text1 = m1[1];
      setDialogueHistory(prev => [...prev, { role: 'ussd', text: text1 }]);
      setPayloadLogs(prev => [{ timestamp: new Date().toLocaleTimeString(), hop: 1, input: '*185#', latency: '65ms', status: 200, rawXml: r1.rawXml }, ...prev]);

      // Step 2: 1234 (Enter PIN -> Main Menu)
      await new Promise(r => setTimeout(r, 600));
      setDialogueHistory(prev => [...prev, { role: 'user', text: '1234' }]);
      const r2 = await sendLiveUssdRequest({ mno, sessionId: currentSession, msisdn, newRequest: '0', input: '1234' });
      let text2 = 'Welcome to Absa Bank\n1. Balance Inquiry\n2. Mini Statement\n3. Funds Transfer\n4. Buy Airtime';
      const m2 = r2.rawXml?.match(/<applicationResponse>([\s\S]*?)<\/applicationResponse>/);
      if (m2 && m2[1]) text2 = m2[1];
      setDialogueHistory(prev => [...prev, { role: 'ussd', text: text2 }]);
      setPayloadLogs(prev => [{ timestamp: new Date().toLocaleTimeString(), hop: 2, input: '1234', latency: '78ms', status: 200, rawXml: r2.rawXml }, ...prev]);

      // Step 3: 1 (Select Menu Option 1 -> Account Balance)
      await new Promise(r => setTimeout(r, 600));
      setDialogueHistory(prev => [...prev, { role: 'user', text: '1' }]);
      const r3 = await sendLiveUssdRequest({ mno, sessionId: currentSession, msisdn, newRequest: '0', input: '1' });
      let text3 = 'Your account balance for Account 100234567 is UGX 1,500,000. Thank you for banking with Absa.';
      const m3 = r3.rawXml?.match(/<applicationResponse>([\s\S]*?)<\/applicationResponse>/);
      if (m3 && m3[1]) text3 = m3[1];
      setDialogueHistory(prev => [...prev, { role: 'ussd', text: text3 }]);
      setPayloadLogs(prev => [{ timestamp: new Date().toLocaleTimeString(), hop: 3, input: '1', latency: '82ms', status: 200, rawXml: r3.rawXml }, ...prev]);
    }

    setLoading(false);
    if (onSessionCompleted) onSessionCompleted();
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '24px' }}>
      
      {/* 1. Phone Simulator Device */}
      <div className="glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Smartphone size={18} color="var(--accent-orange)" />
            <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Virtual Mobile Device</h2>
          </div>
          <button className="btn-secondary" onClick={handleReset} style={{ padding: '6px 12px', fontSize: '12px' }}>
            <RotateCcw size={13} />
            <span>Reset</span>
          </button>
        </div>

        {/* MNO & MSISDN Controls */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '700' }}>MNO Carrier</label>
            <select
              className="custom-select"
              style={{ width: '100%', marginTop: '4px' }}
              value={mno}
              onChange={e => setMno(e.target.value)}
            >
              {mnoOptions?.map(cfg => (
                <option key={cfg.code || cfg.id} value={cfg.code.toLowerCase()}>
                  {cfg.displayName || cfg.code}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: '700' }}>Phone MSISDN</label>
            <input
              type="text"
              value={msisdn}
              onChange={e => setMsisdn(e.target.value)}
              style={{
                width: '100%',
                marginTop: '4px',
                background: '#F8F9FA',
                border: '1px solid var(--border-medium)',
                borderRadius: 'var(--radius-sm)',
                padding: '8px 12px',
                color: 'var(--text-primary)',
                fontSize: '13px',
                fontFamily: 'var(--font-mono)'
              }}
            />
          </div>
        </div>

        {/* Presets */}
        <button
          className="btn-primary"
          onClick={() => handleQuickFlow('BALANCE')}
          disabled={loading}
          style={{ justifyContent: 'center' }}
        >
          <Play size={14} />
          <span>Simulate Flow (*185# → PIN: 1234 → Menu: 1)</span>
        </button>

        {/* LCD Screen */}
        <div style={{
          background: '#0B0F19',
          border: '4px solid #1E293B',
          borderRadius: '20px',
          padding: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          minHeight: '290px',
          boxShadow: '0 12px 30px rgba(0,0,0,0.25)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94A3B8', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '8px' }}>
            <span>ID: {sessionId}</span>
            <span style={{ color: '#F84122', fontWeight: '700' }}>Hops: {hopCount}</span>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto', maxHeight: '240px' }}>
            {dialogueHistory.map((msg, i) => (
              <div
                key={i}
                style={{
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '88%',
                  padding: '9px 13px',
                  borderRadius: '10px',
                  fontSize: '13px',
                  lineHeight: '1.45',
                  background: msg.role === 'user' ? '#F84122' : msg.role === 'error' ? '#EF4444' : 'rgba(255, 255, 255, 0.08)',
                  color: '#FFFFFF',
                  whiteSpace: 'pre-line',
                  fontFamily: 'var(--font-mono)',
                  boxShadow: msg.role === 'user' ? '0 2px 8px rgba(248, 65, 34, 0.4)' : 'none'
                }}
              >
                {msg.text}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <input
              type="text"
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Enter menu input..."
              disabled={loading}
              style={{
                flex: 1,
                background: 'rgba(255, 255, 255, 0.08)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: '8px',
                padding: '8px 12px',
                color: '#FFFFFF',
                fontSize: '13px',
                fontFamily: 'var(--font-mono)',
                outline: 'none'
              }}
            />
            <button className="btn-primary" onClick={() => handleSend()} disabled={loading} style={{ padding: '8px 14px', borderRadius: '8px' }}>
              <Send size={14} />
            </button>
          </div>
        </div>

      </div>

      {/* 2. Live Protocol XML/JSON Inspector */}
      <div className="glass-card" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Terminal size={18} color="var(--accent-orange)" />
          <h2 style={{ fontSize: '16px', fontWeight: '800' }}>Protocol Exchange & Payload Inspector</h2>
        </div>
        <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          Real-time request/response captures with round-trip latency metrics
        </p>

        {payloadLogs.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
            No dialogues executed yet. Send a request from the phone simulator on the left to inspect raw payloads.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', overflowY: 'auto', maxHeight: '520px' }}>
            {payloadLogs.map((log, i) => (
              <div key={i} style={{
                background: '#F8FAFC',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-md)',
                padding: '14px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', fontSize: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '800', color: 'var(--text-primary)' }}>Hop #{log.hop}</span>
                    <span style={{ color: 'var(--accent-orange)', fontFamily: 'var(--font-mono)', fontWeight: '600' }}>Input: "{log.input}"</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ color: 'var(--accent-emerald)', fontWeight: '700' }}>{log.latency}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{log.timestamp}</span>
                  </div>
                </div>

                <pre style={{
                  background: '#0B0F19',
                  border: '1px solid var(--border-medium)',
                  borderRadius: '6px',
                  padding: '10px',
                  fontSize: '11px',
                  color: '#34D399',
                  fontFamily: 'var(--font-mono)',
                  overflowX: 'auto',
                  maxHeight: '120px'
                }}>
                  {log.rawXml}
                </pre>
              </div>
            ))}
          </div>
        )}

      </div>

    </div>
  );
}
